import { DisabledMapOptions } from 'src/Components/Map/Map'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import React, { Context, useMemo, useState } from 'react'
import { useCookies } from 'src/Libs/cookies'
import { useTypedSelector } from 'src/State/RootReducer'

interface Props {
  children?: React.ReactNode
}

interface UserGoogleAgreement {
  googleAgreed: boolean
  noMapOptions: DisabledMapOptions
}

const defaultContext: UserGoogleAgreement = {
  googleAgreed: false,
  noMapOptions: {},
}

const UserGoogleAgreementContext = React.createContext<UserGoogleAgreement>(defaultContext)

const UserGoogleAgreementProvider: React.FC<Props> = ({ children }) => {
  const { cookies, setGoogleAgreed } = useCookies()
  const { googleAgreed } = cookies
  const [googleMapLoading, setGoogleMapLoading] = useState(false)
  const userId = useTypedSelector(state => state.auth.userId)
  const { t } = useCustomTranslation()

  const acceptGoogleMap = (agree: boolean): void => {
    if (userId) {
      setGoogleMapLoading(true)
      setGoogleAgreed(agree)
    }
  }

  const noMapOptions = useMemo<DisabledMapOptions>(
    () => ({
      buttonLabel: t('framework.acceptGoogleMap.buttonText'),
      onClick: () => acceptGoogleMap(true),
      text: t('framework.acceptGoogleMap.text'),
      loading: googleMapLoading,
      disagree: () => acceptGoogleMap(false),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [googleMapLoading]
  )

  const value = useMemo<UserGoogleAgreement>(
    () => ({
      googleAgreed: googleAgreed ?? false,
      noMapOptions,
    }),
    [googleAgreed, noMapOptions]
  )

  return <UserGoogleAgreementContext.Provider value={value}>{children}</UserGoogleAgreementContext.Provider>
}

const useGoogleAgreementContext = (): Context<UserGoogleAgreement> => {
  return UserGoogleAgreementContext
}

export { UserGoogleAgreementProvider, useGoogleAgreementContext }
